/**
 * Comment deep-link consumer (spec A1).
 *
 * Notification emails link straight at a comment:
 *   /app/bids/{bidId}/responses/{artifactId}/sections/{key}?commentId=<id>
 *
 * Mounting this hook on an editor page makes that link land: the AI panel is
 * opened on the Comments tab, and once the comment list for the artifact has
 * actually loaded the target comment is focused and the param is stripped so a
 * refresh (or a Back) doesn't re-fire the jump.
 *
 * Consume rules — the param is a one-shot and must survive until it can be
 * honoured:
 * - Never consume before the first load settles (the list starts undefined).
 * - Loaded, comment present → focus it, strip, no toast.
 * - Loaded, comment absent → it was deleted; toast and strip, focus nothing.
 * - Load failed (settled, list still undefined) → strip silently. The comment
 *   may well exist; a "was deleted" toast would be a lie.
 */

import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useAgentStore } from '@/stores/agent-store'
import { useCommentsStore } from '@/stores/comments-store'
import { useUnifiedAiPanelStore } from '@/stores/unified-ai-panel-store'

export function useCommentDeepLink(artifactId: string): void {
  const [searchParams, setSearchParams] = useSearchParams()
  const commentId = searchParams.get('commentId')

  // Keyed by comment id, not a boolean: the editor route element stays mounted
  // across in-app navigations that only change the search params, so a second
  // notification for another comment on the same artifact is a fresh link and
  // must be honoured. Re-arriving at the *same* id stays consumed.
  const consumedIdRef = useRef<string | null>(null)

  const comments = useCommentsStore((s) => s.byTargetId[artifactId])
  const loading = useCommentsStore((s) => s.loading[artifactId])

  // True once a load has been observed in flight. Needed because a failed load
  // leaves `comments` undefined — indistinguishable from "never loaded".
  const attemptedRef = useRef(false)
  if (loading) attemptedRef.current = true

  // Reveal the Comments tab immediately, before the list resolves, so the user
  // sees where they've landed instead of a blank editor.
  useEffect(() => {
    if (!commentId || consumedIdRef.current === commentId) return
    useAgentStore.getState().openPanel()
    useUnifiedAiPanelStore.getState().setTab('comment')
  }, [commentId])

  useEffect(() => {
    if (!commentId || consumedIdRef.current === commentId || loading) return

    const settled = comments !== undefined || attemptedRef.current
    if (!settled) return

    consumedIdRef.current = commentId

    const target = comments?.find((c) => c.id === commentId)
    if (target) {
      useCommentsStore.getState().focusComment(commentId)
    } else if (comments !== undefined) {
      toast('That comment was deleted')
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('commentId')
        return next
      },
      { replace: true },
    )
  }, [commentId, comments, loading, setSearchParams])
}
